import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, LayoutGrid, List, Search, SlidersHorizontal, X } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import { effectiveStock, isEffectivelySoldOut } from '../../lib/retail'
import { campaignDisplayName, campaignEndDate, hasActiveCompareAt } from '../../lib/menuCampaign'
import { useEffectiveMenu } from '../../hooks/useEffectiveMenu'
import type { MenuItem, CustomCategory, Store } from '../../store/useStore'
import { selectLocalizedName, selectLocalizedDescription } from '../../services/translate'
import { ALLERGEN_INFO, DIETARY_INFO, MENU_CATEGORIES } from './constants'
import DietaryModal from './DietaryModal'
import { NO_IMAGE, galleryOf } from '../../lib/menuImage'
import { type MenuLayout, rememberMenuLayout, savedMenuLayout, storeMenuLayout } from '../../lib/menuLayout'
import { matchesMenuSearch, parseMenuSearch } from '../../lib/menuSearch'
import {
  blockedGroups, groupsForItem, isSelectionComplete, selectionPrice,
  type VariationGroup, type VariationSelection,
} from '../../lib/menuVariation'
import VariationPicker from '../../components/VariationPicker'

interface MenuTabProps {
  menu: MenuItem[]
  /** #1353 放題プラン適用中の卓で 0 円になる商品の id。**サーバが 0 円を確定する**
   *  ので、ここは表示を実際の請求額に合わせるためだけに使う。空なら通常価格。 */
  allYouCanItemIds?: string[]
  userAllergens?: string[]
  userDietary?: string[]
  customCategories?: CustomCategory[]
  /** #2031 `selection` は客が選んだバリエーション (選択肢の無い商品では空)。 */
  onAddToCart?: (item: MenuItem, selection?: VariationSelection) => void
  /** #2031 店のバリエーション定義。商品は ID で参照する (`variationGroupIds`)。 */
  variationGroups?: VariationGroup[] | null
  /** #1531 卓 QR を読むまで注文できない店で、メニューの先頭に出す案内。
   * 従来は店舗情報タブにしか出ておらず、客はボタンが無い理由が分からなかった。 */
  tableRequiredNotice?: string
  /** #1353 上にある sticky なタブバーの実測高さ(px)。カテゴリチップをその**下**に
   *  貼り付けるために使う。0 のままだとタブバーの裏に潜って見えなくなる。 */
  stickyTop?: number
  /** #2029 渡すと「画像 / リスト」の切り替えを出す。最初の表示は店の選択 (`menuLayout`)、
   *  客が切り替えたらこの店についてその端末に記憶する。渡さない (お土産の一覧) は画像だけ。 */
  layoutStore?: Pick<Store, 'id' | 'menuLayout'>
}

/**
 * 二重価格表示（比較対照価格つきの値引き表示）。
 *
 * 景品表示法では、比較対照が**何の価格か**を示さないと有利誤認になりうる。
 * 取り消し線を引くだけにせず、「メーカー希望小売価格」等のラベルを必ず出す。
 *
 * 比較対照が販売価格以下なら**何も出さない**。値引きしていないのに値引きに
 * 見える表示は、サーバ側でも保存時に弾いているが、古いデータや別経路で
 * 入った値が客側に出ないよう、描画側でも同じ条件を持つ。
 */
function ComparePrice({ item, size = 'sm' }: { item: MenuItem; size?: 'sm' | 'lg' }) {
  const { t, locale } = useI18n()
  if (!hasActiveCompareAt(item)) return null
  const compareAt = item.compareAtPrice as number

  const pct = Math.round(((compareAt - item.price) / compareAt) * 100)
  const label = t(item.compareAtPriceKind === 'store'
    ? 'storeDetail.compareAtStore'
    : 'storeDetail.compareAtManufacturer')

  // #1711 キャンペーン名と期限。名称・期限のどちらも無ければ従来どおり「N%OFF」だけ。
  // 期限を付けると「有名無実の定価」という景表法の論点が消えるので、付いているなら
  // 必ず客に見せる (店の内部メモにしない)。
  const campaign = campaignDisplayName(item, locale)
  const end = campaignEndDate(item)
  const until = end
    ? t('storeDetail.campaignUntil', { date: end.toLocaleDateString(locale, { month: 'short', day: 'numeric' }) })
    : null
  // 区切りはロケールごとの句読点にする (ja は「・」、欧文は「 · 」)
  const campaignLine = campaign && until
    ? t('storeDetail.campaignLine', { name: campaign, until })
    : campaign || until

  return (
    <div className={`font-body ${size === 'lg' ? 'text-xs mt-1' : 'text-[10px] mt-0.5'}`}>
      {campaignLine && (
        <p className="font-semibold text-yokoso-red">{campaignLine}</p>
      )}
      <p>
        <span className="text-text-tertiary">
          {label} <span className="line-through">¥{compareAt.toLocaleString()}</span>
        </span>
        <span className="ml-1.5 font-bold text-yokoso-red">
          {t('storeDetail.compareAtOff', { pct: String(pct) })}
        </span>
      </p>
    </div>
  )
}

/**
 * #2031 商品を開いたときの「選ぶ → カートに入れる」。
 *
 * 選んだ内容は**ここに閉じ込める**。親に持たせると、「たれ」を選んだ次に別の商品を
 * 開いたとき前の選択が残る。呼び出し側が key を商品 ID にして作り直させる
 * (ItemGallery と同じ理由)。
 */
function AddToCartBlock({ groups, basePrice, onAdd }: {
  groups: VariationGroup[]
  /** 本体の値段 (放題で 0 円になる商品は 0)。追加料金を足してボタンに出す。 */
  basePrice: number
  onAdd: (selection: VariationSelection) => void
}) {
  const { t } = useI18n()
  const [selection, setSelection] = useState<VariationSelection>({})
  const ready = isSelectionComplete(groups, selection)
  // 追加料金があるときだけボタンに金額を出す。上の値段 (本体) と食い違って見えるのを防ぐ —
  // 「¥900 と書いてあるのにカートでは ¥1,150」を、入れる前に分かるようにする。
  const options = selectionPrice(groups, selection)
  // 必須の項目が全部売り切れ = この商品は今は注文できない。「選んでから…」のままにすると、
  // 客は押せる選択肢が無いのに選べと言われ続ける。
  if (blockedGroups(groups).length > 0) {
    return (
      <>
        <VariationPicker groups={groups} selection={selection} onChange={setSelection} />
        <div className="mt-5 w-full h-12 rounded-xl bg-gray-200 text-text-secondary font-display font-semibold text-sm flex items-center justify-center">
          {t('storeDetail.soldOut')}
        </div>
      </>
    )
  }
  return (
    <>
      <VariationPicker groups={groups} selection={selection} onChange={setSelection} />
      <button
        disabled={!ready}
        onClick={() => onAdd(selection)}
        className="mt-5 w-full h-12 rounded-xl bg-yokoso-red text-white font-display font-semibold text-sm tap-effect active:scale-[0.98] transition-transform disabled:bg-gray-200 disabled:text-text-secondary"
      >
        {/* 押せない理由を**ボタン自身に**書く。灰色のボタンだけだと故障に見える */}
        {!ready
          ? t('variation.chooseFirst')
          : options > 0
            ? t('cart.addToCartWithPrice', { price: `¥${(basePrice + options).toLocaleString()}` })
            : t('cart.addToCart')}
      </button>
    </>
  )
}

/** バッジ表示中のアレルギー (4 つまで + 残り件数)。一覧のカードとリストの行で同じものを出す。 */
function AllergenLine({ item }: { item: MenuItem }) {
  const { t } = useI18n()
  if (!item.allergens || item.allergens.length === 0) return null
  return (
    <div className="flex gap-0.5 mt-1">
      {item.allergens.slice(0, 4).map(a => ALLERGEN_INFO[a] && (
        <span key={a} className="text-[10px]" title={t(ALLERGEN_INFO[a].label)}>
          {ALLERGEN_INFO[a].emoji}
        </span>
      ))}
      {item.allergens.length > 4 && <span className="text-[10px] text-text-tertiary">+{item.allergens.length - 4}</span>}
      {/* #288 AI 推定を店舗が未確認 — 詳細シートに注意文が出る */}
      {item.needsAllergenReview && (
        <span className="text-[10px]" title={t('storeDetail.allergenUnverified')}>⚠️</span>
      )}
    </div>
  )
}

/**
 * 商品を開いたときの写真。2 枚以上あるときだけ送り・戻しとインジケータを出す。
 *
 * 一覧のカードには出さない — カードはメイン 1 枚だけ読む約束で、そこを崩すと
 * 一覧を開いただけで商品数 × 枚数ぶんの画像が飛ぶ（費用が枚数に比例してしまう）。
 */
function ItemGallery({ item, alt }: { item: MenuItem; alt: string }) {
  const { t } = useI18n()
  const urls = galleryOf(item)
  const [i, setI] = useState(0)
  const touchStartX = useRef<number | null>(null)

  // 2 枚目以降は**開いた時点で先に読んでおく**。送りを押してから読みに行くと、
  // Firebase Storage は `Cache-Control: private, max-age=0` で 1 枚 0.6〜1.4 秒
  // (demo 実測) かかり、その間は前の写真のまま止まって見える。1 枚目は一覧の
  // カードで読み済みなので速く、2 枚目だけが遅く感じていた。
  // 読むのは開いた 1 品ぶんだけなので、上の「一覧はメイン 1 枚」の約束は崩れない。
  // urls は毎描画で作り直される配列なので、中身を文字列にして依存に使う。
  const galleryKey = urls.join('\n')
  const preloaded = useRef<HTMLImageElement[]>([])
  useEffect(() => {
    preloaded.current = galleryKey.split('\n').slice(1).map((u) => {
      const img = new Image()
      img.src = u
      // 読み込みだけでなくデコードも済ませ、切り替えた瞬間に出るようにする。
      // 失敗は表示側の onError が受け止めるのでここでは握りつぶす。
      img.decode().catch(() => {})
      return img
    })
    return () => { preloaded.current = [] }
  }, [galleryKey])

  if (urls.length === 0) {
    return <img src={NO_IMAGE} alt="" className="w-full h-56 object-cover" />
  }

  // 端で止めず循環させる（EC の商品ページと同じ挙動）。端で押せなくなると
  // 壊れたように見える。
  const step = (d: number) => setI((n) => (n + d + urls.length) % urls.length)

  return (
    <div
      className="relative"
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX }}
      onTouchEnd={(e) => {
        const from = touchStartX.current
        touchStartX.current = null
        if (from == null || urls.length < 2) return
        const dx = e.changedTouches[0].clientX - from
        // 40px 未満はタップ・縦スクロールの巻き添えとみなす。
        if (Math.abs(dx) < 40) return
        step(dx < 0 ? 1 : -1)
      }}
    >
      <img
        src={urls[i] ?? urls[0]}
        alt={alt}
        className="w-full h-56 object-cover"
        onError={(e) => { e.currentTarget.src = NO_IMAGE }}
      />
      {urls.length > 1 && (
        <>
          <button
            onClick={() => step(-1)}
            aria-label={t('courseDetail.photoPrev')}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/35 tap-effect"
          >
            <ChevronLeft size={16} className="text-white" />
          </button>
          <button
            onClick={() => step(1)}
            aria-label={t('courseDetail.photoNext')}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/35 tap-effect"
          >
            <ChevronRight size={16} className="text-white" />
          </button>
          {/* 何枚目かを数字で出す（EC の商品ページと同じ形）。以前の白いドットは
            * 商品写真に多い白・生成り地の背景に溶けて、ほぼ見えなかった。 */}
          <span className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-2.5 py-0.5 text-[11px] font-medium text-white">
            {i + 1} / {urls.length}
          </span>
        </>
      )}
    </div>
  )
}

export default function MenuTab({ menu: rawMenu, userAllergens = [], userDietary = [], customCategories = [], onAddToCart, tableRequiredNotice, allYouCanItemIds = [], stickyTop = 0, layoutStore, variationGroups }: MenuTabProps) {
  // #1711 期限切れのキャンペーンは通常価格で出す。正はサーバ (API が既に戻して返す) で、
  // ここは**開いたまま期限をまたいだ端末**の保険 (1 分ごとに再評価)。カートへ渡す item もこの後の値。
  const menu = useEffectiveMenu(rawMenu)
  // 放題対象は 0 円で出す。通常価格のまま見せると、客は「課金される」と思って
  // 飲み放題対象のドリンクを頼まなくなる（カート合計も実額とズレる）。
  const coveredIds = new Set(allYouCanItemIds)
  const { t, locale } = useI18n()
  // #1353 セクション見出しへのジャンプと、現在地のハイライトに使う
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const [activeSection, setActiveSection] = useState('')
  // 詳細シートの item は**現在の menu から id で引き直す**。開いたまま期限をまたいだとき、
  // 一覧は再評価で戻るのにシート (とカートへ渡す item) だけ古い価格のまま、にしない。
  const [selectedRaw, setSelectedMenuItem] = useState<MenuItem | null>(null)
  const selectedMenuItem = selectedRaw ? (menu.find((m) => m.id === selectedRaw.id) ?? selectedRaw) : null
  // #2031 開いている商品の選択グループ。JSX の中で毎回作ると、検索の 1 文字ごと・1 分ごとの
  // 再描画で選択肢のボタンが全部描き直される。
  const selectedGroups = useMemo(
    () => groupsForItem({ variationGroups }, selectedMenuItem),
    [variationGroups, selectedMenuItem],
  )
  const [showBadges, setShowBadges] = useState(false)
  // #2029 客がこの店で選んだ表示。選んでいない間 (null) は店の選択に従う — 店が後から
  // 変えても追従する。店が変わったときは呼び出し側が key で作り直す。
  const [chosenLayout, setChosenLayout] = useState<MenuLayout | null>(
    () => (layoutStore ? savedMenuLayout(layoutStore.id) : null),
  )
  const layout: MenuLayout = layoutStore ? (chosenLayout ?? storeMenuLayout(layoutStore)) : 'grid'
  const chooseLayout = (next: MenuLayout) => {
    if (!layoutStore) return
    setChosenLayout(next)
    rememberMenuLayout(layoutStore.id, next)
  }
  const [showDietaryModal, setShowDietaryModal] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(() => {
    try {
      const seen = localStorage.getItem('dietaryOnboardingShown') === 'true'
      const hasPrefs = userAllergens.length > 0 || userDietary.length > 0
      return !seen && !hasPrefs
    } catch {
      return false
    }
  })
  const hideAllergens = userAllergens
  const showDietary = userDietary

  const dismissOnboarding = () => {
    try {
      localStorage.setItem('dietaryOnboardingShown', 'true')
    } catch {
      // ignore storage errors
    }
    setShowOnboarding(false)
  }

  const openDietaryModal = () => {
    dismissOnboarding()
    setShowDietaryModal(true)
  }

  // アレルギー・食事スタイルだけで絞る。**カテゴリでは絞らない** — 下の
  // セクション分けで全カテゴリを縦に並べるため。
  const filteredMenu = menu
    .filter(item => !item.allergens?.some(a => hideAllergens.includes(a)))
    .filter(item => showDietary.length === 0 || showDietary.every(d => item.dietary?.includes(d)))

  // #1353 カテゴリはタブで切り替えず、見出し付きのセクションとして**全部縦に並べる**。
  //
  // タブで切り替える形だと、客は選んだカテゴリしか見ず他の存在に気づかない
  // (= 店が注文機会を落とす)。飲食店のメニューは「めくって全体を眺める」ものなので、
  // 実物のメニューブックと同じ縦スクロールに合わせる。上のチップは絞り込みではなく
  // **その見出しへ飛ぶジャンプ**として使う。
  // いま有効なカテゴリのキー。ここに無い値を持つ品は「その他」へ落とす。
  const knownCategoryKeys = new Set([
    ...MENU_CATEGORIES.map((c) => c.key),
    ...customCategories.map((c) => c.key),
  ])

  const sections = [
    // #1353 店主のおすすめは**先頭に再掲**する。カテゴリではなく強調なので、
    // 各品は本来のカテゴリのセクションにも出る（実物のメニューでも「本日のおすすめ」は
    // 再掲される）。ここでしか出さないと、おすすめにした品がメインから消える。
    { key: '__recommended__', label: t('storeDetail.menuRecommended'), description: '',
      items: filteredMenu.filter((m) => m.recommended) },
    ...MENU_CATEGORIES.filter((c) => c.key !== 'all')
      .map((c) => ({ key: c.key, label: t(c.label), description: '',
        items: filteredMenu.filter((m) => m.category === c.key) })),
    // #2115 店が書いたカテゴリの説明を見出しの下に出す (「世羅みのり牛とは」)。
    // 英語が無い言語は en → ja の順に落とす (アプリ全体の決まりと同じ)
    ...customCategories
      .map((c) => ({
        key: c.key,
        // 訳があればそれ。無ければ英語、それも無ければ日本語 (品名と同じ決まり)
        label: (locale === 'ja' ? c.label
          : (c.translations?.[locale]?.label || c.labelEn || c.label)) || c.label,
        description: (locale === 'ja' ? c.description
          : (c.translations?.[locale]?.description || c.descriptionEn || c.description)) || '',
        items: filteredMenu.filter((m) => m.category === c.key),
      })),
    // 既知のどのカテゴリにも属さない品を全部ここで拾う。対象は 2 種類:
    //   - カテゴリ未設定の品
    //   - **削除されたカスタムカテゴリを指したままの品**（店主がカテゴリを消すと発生）
    // 拾わないと、どのセクションにも出ず**客から見えないまま売れ残る**。
    { key: '__none__', label: t('storeDetail.menuOther'), description: '',
      items: filteredMenu.filter((m) => !m.category || !knownCategoryKeys.has(m.category)) },
  ].filter((s) => s.items.length > 0)

  const hasAnyFilter = hideAllergens.length > 0 || showDietary.length > 0

  // #2028 商品名で探す (日本語・ローマ字。lib/menuSearch.ts)。開いている間は入力欄を出し、
  // 入力があればカテゴリ分けをやめて一致した品だけを 1 列に並べる。アレルギー・食事の設定で
  // 隠している品は結果にも出さない (filteredMenu から探す)。表示の切り替えと同じく
  // layoutStore を渡した一覧 (料理のメニュー) だけ。
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const searchQuery = layoutStore && searchOpen ? parseMenuSearch(searchText) : null
  const searchResults = searchQuery ? filteredMenu.filter((m) => matchesMenuSearch(m, searchQuery)) : null
  const closeSearch = () => {
    setSearchOpen(false)
    setSearchText('')
  }

  // タブバー + チップ行の分だけ上に余白を取る。決め打ちにすると、タブ名が
  // 折り返す狭い端末で見出しが sticky 帯の裏に隠れる。
  const CHIP_ROW_H = 44
  const scrollOffset = stickyTop + CHIP_ROW_H + 8

  // スクロール位置に合わせてチップをハイライトし、そのチップを可視域へ寄せる。
  // これが無いと、カテゴリが増えたときに**画面外のチップの存在に気づけない**
  // (「店主のおすすめ」が見えない、という状態がまさにこれだった)。
  const sectionKeys = sections.map((s) => s.key).join(',')
  useEffect(() => {
    const keys = sectionKeys ? sectionKeys.split(',') : []
    if (keys.length === 0) return
    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (hit) setActiveSection(hit.target.getAttribute('data-section') || '')
      },
      // sticky 帯のすぐ下に入った見出しを「現在地」とみなす
      { rootMargin: `-${scrollOffset}px 0px -70% 0px`, threshold: 0 },
    )
    keys.forEach((k) => {
      const el = sectionRefs.current[k]
      if (el) io.observe(el)
    })
    return () => io.disconnect()
  }, [sectionKeys, scrollOffset])

  useEffect(() => {
    const chip = chipRefs.current[activeSection]
    chip?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [activeSection])

  const jumpTo = (key: string) => {
    const el = sectionRefs.current[key]
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - scrollOffset
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  // #2029 リスト / 画像の一覧。#2028 カテゴリのセクションと検索の結果で同じものを使う
  const renderItems = (items: MenuItem[]) => (
    layout === 'list' ? (
      /* #2029 リスト。写真は出さない・読まない (押すと下の詳細で写真と説明が出る)。
       * 名前は折り返して全部出す (カードと違って横幅があるので切らない)。 */
      <div className="space-y-2">
        {items.map((item) => {
          const soldOut = isEffectivelySoldOut(item, menu)
          const covered = coveredIds.has(item.id)
          const stock = soldOut ? null : effectiveStock(item, menu)
          return (
            <button
              key={item.id}
              onClick={() => setSelectedMenuItem(item)}
              className="flex w-full items-start gap-3 rounded-xl bg-white px-3.5 py-3 text-left shadow-card animate-fade-in tap-card"
            >
              <div className="min-w-0 flex-1">
                {soldOut && (
                  <span className="mb-1 inline-block rounded bg-gray-500 px-1.5 py-0.5 font-body text-[10px] font-bold text-white">
                    {t('storeDetail.soldOut')}
                  </span>
                )}
                <p className={`font-body text-sm leading-snug ${soldOut ? 'text-text-tertiary' : 'font-semibold text-text-primary'}`}>
                  {/* #1353 店主のおすすめはカードと同じく品そのものに印を付ける */}
                  {item.recommended && <span className="mr-1 text-gold">★</span>}
                  {locale === 'ja' ? item.name : selectLocalizedName(item, locale)}
                </p>
                {locale !== 'ja' && (
                  <p className="mt-0.5 font-jp text-xs leading-snug text-text-tertiary">{item.name}</p>
                )}
                {showBadges && item.dietary && item.dietary.length > 0 && (
                  <div className="mt-1 flex gap-0.5">
                    {item.dietary.map(d => DIETARY_INFO[d] && (
                      <span key={d} className={`rounded-full px-1 py-0.5 text-[8px] font-bold ${DIETARY_INFO[d].color}`}>
                        {DIETARY_INFO[d].emoji}
                      </span>
                    ))}
                  </div>
                )}
                {showBadges && <AllergenLine item={item} />}
                {/* #1181 数量在庫のある商品は残数を出す (カードと同じ条件) */}
                {stock != null && (
                  <p className={`mt-0.5 font-body text-[10px] ${stock <= 5 ? 'font-semibold text-yokoso-red' : 'text-text-tertiary'}`}>
                    {t('storeDetail.stockLeft', { count: String(stock) })}
                  </p>
                )}
              </div>
              <div className="shrink-0 text-right">
                <p className={`font-body text-sm font-bold ${soldOut ? 'text-text-tertiary' : covered ? 'text-matcha' : 'text-gold'}`}>
                  {covered ? '¥0' : `¥${item.price.toLocaleString()}`}
                </p>
                {covered && (
                  <p className="font-body text-[10px] text-matcha">{t('allYouCan.coveredBadge')}</p>
                )}
                {/* 二重価格表示。放題で ¥0 の品には出さない (カードと同じ) */}
                {!covered && <ComparePrice item={item} />}
              </div>
            </button>
          )
        })}
      </div>
    ) : (
    <div className="grid grid-cols-3 gap-3">
      {items.map((item) => (
      <button
        key={item.id}
        onClick={() => setSelectedMenuItem(item)}
        className="rounded-xl overflow-hidden shadow-card bg-white animate-fade-in text-left tap-card"
      >
        <div className="relative">
          <img
            src={item.imageUrl || NO_IMAGE}
            className="w-full aspect-square object-cover bg-gray-100"
            onError={(e) => {
              e.currentTarget.src = NO_IMAGE
            }}
          />
          {showBadges && item.dietary && item.dietary.length > 0 && (
            <div className="absolute top-1 left-1 flex gap-0.5">
              {item.dietary.map(d => DIETARY_INFO[d] && (
                <span key={d} className={`rounded-full px-1 py-0.5 text-[8px] font-bold ${DIETARY_INFO[d].color}`}>
                  {DIETARY_INFO[d].emoji}
                </span>
              ))}
            </div>
          )}
          {/* #1353 店主のおすすめ。カテゴリのセクションに出ているときも
              「これは推し」と分かるよう、品そのものに印を付ける。 */}
          {item.recommended && (
            <span className="absolute top-1 right-1 rounded-full bg-gold px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
              ★
            </span>
          )}
          {/* #970 売り切れオーバーレイ。#1379 セット商品は中身が尽きたら
            * 自動で売り切れ (親に soldOut を持たせて同期させると必ずズレる)。 */}
          {isEffectivelySoldOut(item, menu) && (
            <div className="absolute inset-0 bg-white/65 flex items-center justify-center">
              <span className="rounded-full bg-red-600 text-white px-2.5 py-1 text-[11px] font-bold font-body">
                {t('storeDetail.soldOut')}
              </span>
            </div>
          )}
        </div>
        <div className="p-2">
          {/* Locale-primary title: customer's own language first, JP as reference (#333) */}
          <p className="text-sm text-text-primary leading-tight truncate font-body">
            {locale === 'ja' ? item.name : selectLocalizedName(item, locale)}
          </p>
          {locale !== 'ja' && (
            <p className="font-jp text-xs text-text-tertiary mt-0.5 leading-tight truncate">
              {item.name}
            </p>
          )}
          {showBadges && <AllergenLine item={item} />}
          <p className="text-gold font-bold text-sm font-body mt-1">
            {coveredIds.has(item.id) ? (
              <span className="text-matcha">
                ¥0
                <span className="ml-1 text-[10px] font-normal">{t('allYouCan.coveredBadge')}</span>
              </span>
            ) : `¥${item.price.toLocaleString()}`}
          </p>
          {/* 二重価格表示。食べ放題で ¥0 になっている品には出さない
            * (¥0 の横に「50%OFF」が並ぶと何の割引か分からなくなる)。 */}
          {!coveredIds.has(item.id) && <ComparePrice item={item} />}
          {/* #1181 数量在庫のある商品 (お土産) は残数を表示 — 売り切れ前の
            * 「残りわずか」が購入判断に効く。stock 未設定 (在庫管理しない) は非表示。 */}
          {!isEffectivelySoldOut(item, menu) && effectiveStock(item, menu) != null && (
            <p className={`text-[10px] font-body mt-0.5 ${(effectiveStock(item, menu) as number) <= 5 ? 'text-yokoso-red font-semibold' : 'text-text-tertiary'}`}>
              {t('storeDetail.stockLeft', { count: String(effectiveStock(item, menu)) })}
            </p>
          )}
        </div>
      </button>
      ))}
    </div>
    )
  )

  return (
    <div className="px-4 pt-4">
      {/* #1531 卓 QR を読むまで注文できない店の案内。ボタンだけ消すと客は
        * 「壊れている」と思う — 何をすればいいかをメニューの先頭で伝える */}
      {tableRequiredNotice && (
        <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 font-body text-xs leading-relaxed text-amber-800">
          {tableRequiredNotice}
        </p>
      )}
      {/* Dietary Onboarding Banner */}
      {showOnboarding && (
        <div className="mb-3 rounded-xl bg-gradient-to-r from-navy to-navy-light p-4 text-white animate-fade-in">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="font-display text-sm font-bold">
                🍽️ {t('storeDetail.onboardingTitle')}
              </p>
              <p className="mt-1 font-body text-xs opacity-90 leading-relaxed">
                {t('storeDetail.onboardingDesc')}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={openDietaryModal}
                  className="rounded-lg bg-white px-3 py-1.5 text-xs font-bold text-navy"
                >
                  {t('storeDetail.onboardingSetup')}
                </button>
                <button
                  onClick={dismissOnboarding}
                  className="text-xs text-white/80 underline"
                >
                  {t('storeDetail.onboardingSkip')}
                </button>
              </div>
            </div>
            <button
              onClick={dismissOnboarding}
              className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-white/20 tap-effect"
              aria-label="close"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* #1353 カテゴリチップ = 絞り込みではなく**ジャンプ**。
        * sticky で常に手元に置き、右端をフェードさせて「まだ先がある」と示す。
        * 以前は横スクロールできる手がかりが無く、画面外のカテゴリ (店主のおすすめ) に
        * 気づけなかった。
        *
        * #2028 検索もこの帯に置く。**スクロールしても動かない場所**でないと、下の方まで
        * 見てから探したくなったときに毎回上まで戻ることになる。アイコンだけでは何のボタンか
        * 分からないので「検索」の文字も出す。開いている間は帯ごと入力欄に変わる。 */}
      {(sections.length > 1 || layoutStore) && (
        <div
          className="sticky z-30 -mx-4 mt-3 bg-white/95 backdrop-blur-sm border-b border-gray-100"
          style={{ top: stickyTop }}
        >
          {layoutStore && searchOpen ? (
            <div className="flex items-center gap-2 px-4 py-2">
              <div className="relative flex-1">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                <input
                  type="search"
                  autoFocus
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') closeSearch() }}
                  placeholder={t('storeDetail.menuSearchPlaceholder')}
                  aria-label={t('storeDetail.menuSearchPlaceholder')}
                  className="h-9 w-full rounded-full bg-gray-100 pl-9 pr-3 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-navy/20"
                />
              </div>
              <button
                type="button"
                onClick={closeSearch}
                className="shrink-0 font-body text-sm font-medium text-yokoso-red"
              >
                {t('storeDetail.menuSearchCancel')}
              </button>
            </div>
          ) : (
          <div className="relative flex items-center">
            {layoutStore && (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="ml-4 flex shrink-0 items-center gap-1 rounded-full bg-gray-100 px-3 py-1.5 font-body text-xs font-medium text-text-secondary tap-effect"
              >
                <Search size={14} />
                {t('storeDetail.menuSearchOpen')}
              </button>
            )}
            {/* #2115 アレルギー・食事の絞り込み。**検索と同じ帯に常に置く。**
              * 以前は最初に出る案内のバナーからしか開けず、閉じると入口が消えていた
              * (設定していない人には開く手段が無かった)。設定済みなら件数を出す。
              * 中身はプロフィールの設定そのもの — ここで変えると次のお店にも効く。 */}
            <button
              type="button"
              onClick={openDietaryModal}
              aria-label={t('storeDetail.dietaryFilter')}
              className={`${layoutStore ? 'ml-2' : 'ml-4'} flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 font-body text-xs font-medium tap-effect ${
                hasAnyFilter ? 'bg-navy text-white' : 'bg-gray-100 text-text-secondary'
              }`}
            >
              <SlidersHorizontal size={14} />
              {hasAnyFilter
                ? t('storeDetail.dietaryFilterOn', { count: hideAllergens.length + showDietary.length })
                : t('storeDetail.dietaryFilter')}
            </button>
            <div className="overflow-x-auto scrollbar-hide px-4 py-2">
              <div className="flex gap-2 min-w-max">
                {sections.map((s) => {
                  const isActive = activeSection === s.key
                  return (
                    <button
                      key={s.key}
                      ref={(el) => { chipRefs.current[s.key] = el }}
                      onClick={() => jumpTo(s.key)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? 'bg-navy text-white font-semibold'
                          : 'bg-gray-100 text-text-secondary'
                      }`}
                    >
                      {s.label}
                      <span className={`ml-1 font-normal ${isActive ? 'opacity-75' : 'text-text-tertiary'}`}>
                        {s.items.length}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-white to-transparent" />
          </div>
          )}
        </div>
      )}

      {/* Compact dietary indicator — shown only when filter affects menu
        * #2029 表示の切り替え (画像 / リスト) と同じ行に置く */}
      {(() => {
        const dietaryExcludedCount = menu.filter(item =>
          item.allergens?.some(a => hideAllergens.includes(a)) ||
          (showDietary.length > 0 && !showDietary.every(d => item.dietary?.includes(d)))
        ).length
        const hasDietary = userAllergens.length > 0 || userDietary.length > 0
        const showDietaryControls = hasDietary || showBadges
        if (!showDietaryControls && !layoutStore) return null
        const layoutOptions = [
          { value: 'grid' as const, icon: LayoutGrid, labelKey: 'storeDetail.menuLayoutGrid' },
          { value: 'list' as const, icon: List, labelKey: 'storeDetail.menuLayoutList' },
        ]
        return (
          <div className="mt-2 flex items-center justify-between gap-2">
            {layoutStore ? (
              <div
                role="radiogroup"
                aria-label={t('storeDetail.menuLayoutLabel')}
                className="inline-flex rounded-lg bg-gray-100 p-0.5"
              >
                {layoutOptions.map(({ value, icon: Icon, labelKey }) => {
                  const selected = layout === value
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => chooseLayout(value)}
                      className={`flex items-center gap-1 rounded-md px-2.5 py-1 font-body text-xs transition-colors ${
                        selected ? 'bg-white font-semibold text-navy shadow-sm' : 'text-text-tertiary'
                      }`}
                    >
                      <Icon size={14} />
                      {t(labelKey)}
                    </button>
                  )
                })}
              </div>
            ) : <span />}
            {showDietaryControls && (
            <div className="flex items-center justify-end gap-2">
            {hasDietary && dietaryExcludedCount > 0 && (
              <button
                onClick={() => setShowDietaryModal(true)}
                className="rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] text-blue-700 hover:bg-blue-100 transition-colors"
              >
                ℹ️ {t('storeDetail.dietaryExcludedShort', { count: dietaryExcludedCount })}
              </button>
            )}
            <button
              onClick={() => setShowBadges(!showBadges)}
              className={`text-[10px] underline transition-colors ${
                showBadges ? 'text-navy' : 'text-text-tertiary hover:text-text-primary'
              }`}
            >
              {showBadges ? t('storeDetail.hideBadges') : t('storeDetail.showBadges')}
            </button>
            </div>
            )}
          </div>
        )
      })()}

      {/* #1353 カテゴリごとのセクションを縦に並べる（実物のメニューブックと同じ）。
        * 件数の打ち切りはしない — 「すべてのメニューを見る」で隠すと、隠れた品は
        * 事実上注文されない。 */}
      {/* #2115 **`¥` は日本円と中国元の両方で使われる。** 日本で使う前提なので
        * 誤読は起きにくいが、日本語以外のときだけ 1 行で日本円と書いておく
        * (価格 1 つずつに JPY を足すと、品数の多い店で読みにくくなる)。
        * 税込であることも、外国のお客様には分かりにくいので同じ行で伝える。 */}
      {locale !== 'ja' && (
        <p className="mt-3 font-body text-[11px] text-text-tertiary">
          {t('storeDetail.priceNoteForeign')}
        </p>
      )}

      {searchResults ? (
        /* #2028 検索の結果。カテゴリ分けをやめて、一致した品だけを今の表示 (画像 / リスト) で並べる */
        <div>
          <p className="mt-4 mb-2 font-body text-xs text-text-tertiary">
            {t('storeDetail.menuSearchCount', { count: String(searchResults.length) })}
          </p>
          {searchResults.length > 0 ? renderItems(searchResults) : (
            <div className="flex flex-col items-center justify-center rounded-xl bg-gray-50 py-10">
              <Search size={28} className="mb-2 text-text-tertiary" />
              <p className="px-4 text-center font-body text-sm text-text-secondary">
                {t('storeDetail.menuSearchNoResult', { query: searchText.trim() })}
              </p>
            </div>
          )}
        </div>
      ) : filteredMenu.length > 0 ? (
        sections.map((section) => (
          <div
            key={section.key}
            ref={(el) => { sectionRefs.current[section.key] = el }}
            data-section={section.key}
            className="scroll-mt-24"
          >
            <h3 className="mt-5 mb-1 font-display text-sm font-bold text-text-primary">
              {section.label}
              <span className="ml-1.5 font-body text-xs font-normal text-text-tertiary">
                {section.items.length}
              </span>
            </h3>
            {!!section.description && (
              <p className="mb-2 font-body text-xs leading-relaxed text-text-secondary whitespace-pre-line">
                {section.description}
              </p>
            )}
            {renderItems(section.items)}
          </div>
        ))
      ) : (
        <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl mt-4">
          <p className="text-3xl mb-2">🍽️</p>
          <p className="text-text-secondary font-body text-sm">
            {hasAnyFilter ? t('storeDetail.noMenuFiltered') : t('storeDetail.noMenu')}
          </p>
          {/* カテゴリ絞り込みは廃止したので、残る要因はアレルギー / 食事スタイルのみ。
              そこへ直接飛ばす（「解除」より設定を見せた方が客は納得できる）。 */}
          {hasAnyFilter && (
            <button
              onClick={() => setShowDietaryModal(true)}
              className="mt-3 text-xs text-yokoso-red font-medium underline"
            >
              {t('storeDetail.dietarySettings')}
            </button>
          )}
        </div>
      )}

      {/* Dietary Settings Modal */}
      <DietaryModal open={showDietaryModal} onClose={() => setShowDietaryModal(false)} />

      {/* Menu Item Modal */}
      {selectedMenuItem && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-5 animate-fade-in"
          onClick={() => setSelectedMenuItem(null)}
        >
          <div
            className="relative w-full max-w-sm bg-white rounded-2xl overflow-hidden shadow-xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedMenuItem(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 tap-effect"
            >
              <X size={16} className="text-white" />
            </button>

            {/* key を品の id にして**作り直させる**。何枚目を見ているかの状態が
              * 残ると、「3 枚目」を見た次に 2 枚しか無い品を開いて空になる。
              * effect で戻すより、作り直す方が余計な再描画が無い。 */}
            <ItemGallery
              key={selectedMenuItem.id}
              item={selectedMenuItem}
              alt={locale === 'ja' ? selectedMenuItem.name : selectLocalizedName(selectedMenuItem, locale)}
            />

            <div className="p-5">
              {/* Locale-primary title: customer's own language first, JP as reference (#333) */}
              <h3 className="font-body font-bold text-lg text-text-primary">
                {locale === 'ja' ? selectedMenuItem.name : selectLocalizedName(selectedMenuItem, locale)}
              </h3>
              {locale !== 'ja' && (
                <p className="font-jp text-text-tertiary text-xs mt-0.5">
                  {selectedMenuItem.name}
                </p>
              )}

              {selectedMenuItem.description && (
                <p className="font-jp text-sm text-text-secondary mt-3 leading-relaxed whitespace-pre-line">
                  {selectLocalizedDescription(selectedMenuItem, locale) ?? selectedMenuItem.description}
                </p>
              )}

              {/* #2115 **詳細を開いた画面では、アレルギーは常に出す。**
                * 以前は一覧と同じ「アレルギー表示」トグル (既定 OFF) の裏にあり、
                * 一方で未確認の注意文だけはトグルに関わらず出ていたため、
                * **注意だけ出て中身が無い**という一番不安な見え方になっていた。
                * 一覧のバッジは今までどおりトグルで畳める (賑やかさの調整はそこで足りる)。 */}
              {selectedMenuItem.allergens && selectedMenuItem.allergens.length > 0 && (
                <div className="mt-3">
                  <p className="font-body text-xs text-text-tertiary mb-1">{t('storeDetail.allergenLabel')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedMenuItem.allergens.map(a => ALLERGEN_INFO[a] && (
                      <span key={a} className="flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-600">
                        <span>{ALLERGEN_INFO[a].emoji}</span>
                        <span>{t(ALLERGEN_INFO[a].label)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedMenuItem.dietary && selectedMenuItem.dietary.length > 0 && (
                <div className="mt-2">
                  <div className="flex flex-wrap gap-1.5">
                    {selectedMenuItem.dietary.map(d => DIETARY_INFO[d] && (
                      <span key={d} className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${DIETARY_INFO[d].color}`}>
                        <span>{DIETARY_INFO[d].emoji}</span>
                        <span>{t(DIETARY_INFO[d].label)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* #288 AI 推定を店舗が未確認の品 — アレルギーを持つ客の安全情報なので
                * showBadges トグルに関わらず常に表示する */}
              {selectedMenuItem.needsAllergenReview && (
                <p className="mt-3 rounded-lg bg-amber-50 px-2.5 py-1.5 font-body text-xs text-amber-800">
                  ⚠️ {t('storeDetail.allergenUnverified')}
                </p>
              )}

              <p className="text-gold font-bold text-2xl font-mono mt-4">
                {coveredIds.has(selectedMenuItem.id) ? (
                  <span className="text-matcha">
                    ¥0
                    <span className="ml-2 text-xs font-body font-normal">{t('allYouCan.coveredBadge')}</span>
                  </span>
                ) : `¥${selectedMenuItem.price.toLocaleString()}`}
              </p>
              {!coveredIds.has(selectedMenuItem.id) && <ComparePrice item={selectedMenuItem} size="lg" />}
              {!isEffectivelySoldOut(selectedMenuItem, menu) && effectiveStock(selectedMenuItem, menu) != null && (
                <p className={`font-body text-xs mt-1 ${(effectiveStock(selectedMenuItem, menu) as number) <= 5 ? 'text-yokoso-red font-semibold' : 'text-text-tertiary'}`}>
                  {t('storeDetail.stockLeft', { count: String(effectiveStock(selectedMenuItem, menu)) })}
                </p>
              )}

              {onAddToCart && (
                isEffectivelySoldOut(selectedMenuItem, menu) ? (
                  // #970 売り切れは追加不可
                  <div className="mt-5 w-full h-12 rounded-xl bg-gray-200 text-text-secondary font-display font-semibold text-sm flex items-center justify-center">
                    {t('storeDetail.soldOut')}
                  </div>
                ) : (
                  <AddToCartBlock
                    key={selectedMenuItem.id}
                    groups={selectedGroups}
                    basePrice={coveredIds.has(selectedMenuItem.id) ? 0 : selectedMenuItem.price}
                    onAdd={(selection) => {
                      onAddToCart(selectedMenuItem, selection)
                      setSelectedMenuItem(null)
                    }}
                  />
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
